const REMOTE_BLOCK_SELECTION_ATTRIBUTE = 'data-remote-block-selection';
const REMOTE_BLOCK_SELECTION_COLOR = '--remote-block-selection-color';
const REMOTE_BLOCK_SELECTION_OVERLAY_ATTRIBUTE =
  'data-remote-block-selection-overlay';
const REMOTE_BLOCK_SELECTION_AVATAR_TARGET_ATTRIBUTE =
  'data-remote-block-selection-avatar-target';
const REMOTE_BLOCK_SELECTION_AVATAR_LAYER_ATTRIBUTE =
  'data-remote-block-selection-avatar-layer';
const remoteBlockSelectionOverlays = new WeakMap<HTMLElement, HTMLElement>();
const remoteBlockSelectionAvatarLayers = new WeakMap<
  HTMLElement,
  HTMLElement
>();
const remoteBlockSelectionAvatarTargets = new WeakMap<
  HTMLElement,
  Map<number, HTMLElement>
>();
const remoteBlockSelectionPreviousOverflow = new WeakMap<
  HTMLElement,
  { priority: string; value: string }
>();

export interface RemoteBlockSelectionAvatar {
  clientId: number;
  right: number;
  top: number;
}

export interface RemoteBlockSelectionPresenceAvatar extends RemoteBlockSelectionAvatar {
  color: string;
}

export type RemoteBlockSelectionPresenceAvatarInput = Omit<
  RemoteBlockSelectionPresenceAvatar,
  'right' | 'top'
>;

export function resolveRemoteBlockSelectionAvatarAnchor(
  avatarIndex: number,
  avatarSize: number,
  stackOffset: number
): Pick<RemoteBlockSelectionAvatar, 'right' | 'top'> {
  return {
    // The avatar box is positioned from a zero-sized anchor. Its right and
    // bottom edges align with the selected block's top-right corner; each
    // additional selector stacks left without escaping that block edge.
    right: avatarSize + avatarIndex * stackOffset,
    top: -avatarSize,
  };
}

export interface RemoteBlockSelectionLayerBinding {
  awaitsBlockView: boolean;
  blockView: HTMLElement | null;
  renderGlobalRect: boolean;
}

export function resolveRemoteBlockSelectionLayerBinding(
  singleBlockSelection: boolean,
  candidateView: unknown
): RemoteBlockSelectionLayerBinding {
  if (!singleBlockSelection) {
    return {
      awaitsBlockView: false,
      blockView: null,
      renderGlobalRect: true,
    };
  }

  const blockView = candidateView instanceof HTMLElement ? candidateView : null;
  return {
    awaitsBlockView: blockView === null,
    blockView,
    // A single DOM-backed block must never flash on the global presence layer
    // while its view is still hydrating. Retry the binding instead.
    renderGlobalRect: false,
  };
}

function createRemoteBlockSelectionOverlay(
  view: HTMLElement,
  color: string,
  rotate: number
): HTMLElement {
  const overlay = document.createElement('div');
  overlay.setAttribute(REMOTE_BLOCK_SELECTION_OVERLAY_ATTRIBUTE, 'true');
  Object.assign(overlay.style, {
    position: 'absolute',
    inset: '0',
    boxSizing: 'border-box',
    border: `2px solid ${color}`,
    borderRadius: '4px',
    pointerEvents: 'none',
    transformOrigin: 'center center',
    transform: `rotate(${rotate}deg)`,
    zIndex: '10',
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.72)',
  });
  view.append(overlay);
  return overlay;
}

function revealRemoteBlockSelectionOverflow(view: HTMLElement): void {
  if (!remoteBlockSelectionPreviousOverflow.has(view)) {
    remoteBlockSelectionPreviousOverflow.set(view, {
      priority: view.style.getPropertyPriority('overflow'),
      value: view.style.getPropertyValue('overflow'),
    });
  }
  // The avatar is intentionally anchored above the selected block. Some
  // block hosts clip their own content, so open only the decorated host for
  // the lifetime of the remote selection.
  view.style.setProperty('overflow', 'visible', 'important');
}

function restoreRemoteBlockSelectionOverflow(view: HTMLElement): void {
  const previous = remoteBlockSelectionPreviousOverflow.get(view);
  if (!previous) {
    return;
  }
  if (previous.value) {
    view.style.setProperty('overflow', previous.value, previous.priority);
  } else {
    view.style.removeProperty('overflow');
  }
  remoteBlockSelectionPreviousOverflow.delete(view);
}

function createRemoteBlockSelectionAvatarTarget(
  layer: HTMLElement,
  avatar: RemoteBlockSelectionAvatar
): HTMLElement {
  const target = document.createElement('div');
  target.setAttribute(REMOTE_BLOCK_SELECTION_AVATAR_TARGET_ATTRIBUTE, 'true');
  target.setAttribute('data-client-id', `${avatar.clientId}`);
  Object.assign(target.style, {
    position: 'absolute',
    top: `${avatar.top}px`,
    right: `${avatar.right}px`,
    width: '0',
    height: '0',
    pointerEvents: 'none',
    transformOrigin: 'left top',
    // This is local to the selected block's stacking context. The block
    // LayerManager position, not a global fixed z-index, controls ordering
    // against other canvas blocks.
    zIndex: '11',
  });
  layer.append(target);
  return target;
}

function resolveRemoteBlockSelectionAvatarLayer(
  view: HTMLElement,
  rotate: number
): HTMLElement {
  const layer =
    remoteBlockSelectionAvatarLayers.get(view) ?? document.createElement('div');
  layer.setAttribute(REMOTE_BLOCK_SELECTION_AVATAR_LAYER_ATTRIBUTE, 'true');
  Object.assign(layer.style, {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    transform: `rotate(${rotate}deg)`,
    transformOrigin: 'center center',
    // This layer follows the exact same rotation as the presence border while
    // remaining local to the selected block's LayerManager stacking context.
    zIndex: '11',
  });
  if (!layer.parentElement) {
    view.append(layer);
  }
  remoteBlockSelectionAvatarLayers.set(view, layer);
  return layer;
}

function updateRemoteBlockSelectionAvatarTargets(
  view: HTMLElement,
  rotate: number,
  avatars: readonly RemoteBlockSelectionAvatar[]
): Map<number, HTMLElement> {
  const targets = remoteBlockSelectionAvatarTargets.get(view) ?? new Map();
  const layer = resolveRemoteBlockSelectionAvatarLayer(view, rotate);
  const nextClientIds = new Set(avatars.map(avatar => avatar.clientId));

  for (const [clientId, target] of targets) {
    if (!nextClientIds.has(clientId)) {
      target.remove();
      targets.delete(clientId);
    }
  }

  for (const avatar of avatars) {
    const target =
      targets.get(avatar.clientId) ??
      createRemoteBlockSelectionAvatarTarget(layer, avatar);
    target.style.top = `${avatar.top}px`;
    target.style.right = `${avatar.right}px`;
    targets.set(avatar.clientId, target);
  }

  remoteBlockSelectionAvatarTargets.set(view, targets);
  return targets;
}

export function applyRemoteBlockSelectionDecoration(
  view: HTMLElement,
  color: string,
  rotate = 0,
  avatars: readonly RemoteBlockSelectionAvatar[] = []
): Map<number, HTMLElement> {
  view.setAttribute(REMOTE_BLOCK_SELECTION_ATTRIBUTE, 'true');
  view.style.setProperty(REMOTE_BLOCK_SELECTION_COLOR, color);
  revealRemoteBlockSelectionOverflow(view);
  const overlay =
    remoteBlockSelectionOverlays.get(view) ??
    createRemoteBlockSelectionOverlay(view, color, rotate);
  overlay.style.border = `2px solid ${color}`;
  overlay.style.transform = `rotate(${rotate}deg)`;
  remoteBlockSelectionOverlays.set(view, overlay);
  return updateRemoteBlockSelectionAvatarTargets(view, rotate, avatars);
}

export function initializeRemoteBlockSelectionDisplay<ColorManager>(
  createColorManager: () => ColorManager,
  setColorManager: (colorManager: ColorManager) => void,
  updateTransform: () => void,
  updateRemoteRects: () => void
): void {
  setColorManager(createColorManager());
  updateTransform();
  updateRemoteRects();
}

export function applyVisibleRemoteBlockSelectionDecoration<
  Avatar extends RemoteBlockSelectionPresenceAvatarInput,
>(
  view: HTMLElement,
  rotate: number,
  avatars: readonly Avatar[],
  visibleClientIds: ReadonlySet<number> | null,
  avatarSize: number,
  stackOffset: number
): {
  avatarTargets: Map<number, HTMLElement>;
  visibleAvatars: Array<Avatar & RemoteBlockSelectionAvatar>;
} {
  const visibleAvatars = [
    ...(visibleClientIds === null
      ? avatars
      : avatars.filter(avatar => visibleClientIds.has(avatar.clientId))),
  ]
    .sort((first, second) => first.clientId - second.clientId)
    .map((avatar, index) => ({
      ...avatar,
      ...resolveRemoteBlockSelectionAvatarAnchor(
        index,
        avatarSize,
        stackOffset
      ),
    }));
  if (visibleAvatars.length === 0) {
    clearRemoteBlockSelectionDecoration(view);
    return { avatarTargets: new Map(), visibleAvatars };
  }

  return {
    avatarTargets: applyRemoteBlockSelectionDecoration(
      view,
      visibleAvatars[0].color,
      rotate,
      visibleAvatars
    ),
    visibleAvatars,
  };
}

export function clearRemoteBlockSelectionDecoration(view: HTMLElement): void {
  remoteBlockSelectionOverlays.get(view)?.remove();
  remoteBlockSelectionOverlays.delete(view);
  remoteBlockSelectionAvatarTargets
    .get(view)
    ?.forEach(target => target.remove());
  remoteBlockSelectionAvatarTargets.delete(view);
  remoteBlockSelectionAvatarLayers.get(view)?.remove();
  remoteBlockSelectionAvatarLayers.delete(view);
  view.removeAttribute(REMOTE_BLOCK_SELECTION_ATTRIBUTE);
  view.style.removeProperty(REMOTE_BLOCK_SELECTION_COLOR);
  restoreRemoteBlockSelectionOverflow(view);
}
